/**
 * Thin HTTP client for grail backend.
 * Auth goes through `artificeFetch` (cookies + X-Artifice-Access, never Bearer).
 */

import { oroborosBrand } from '../brand';
import { artificeFetch } from '../../api/artificeHttp';

export function oroborosApiUrl(path: string): string {
  const base = oroborosBrand.apiBaseUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/** JSON Content-Type + Oroboros CSRF hint. */
export function oroborosJsonHeaders(extra?: Record<string, string>): Record<string, string> {
  return { 'Content-Type': 'application/json', ...(extra || {}) };
}

/**
 * Standard Oroboros API headers. Cookie session plus in-tab Artifice access.
 * Pass `includeTelegramInitData: true` only for pre-cookie bootstrap (Telegram WebView mint).
 */
export function oroborosAuthHeaders(opts?: {
  includeTelegramInitData?: boolean;
  extra?: Record<string, string>;
}): Record<string, string> {
  const headers: Record<string, string> = {
    ...oroborosJsonHeaders(),
    'X-Requested-With': 'Oroboros',
    ...(opts?.extra || {}),
  };
  if (opts?.includeTelegramInitData) {
    try {
      const initData = String((window as any)?.Telegram?.WebApp?.initData || '').trim();
      if (initData) headers['x-telegram-init-data'] = initData;
    } catch {
      /* ignore */
    }
  }
  return headers;
}

export async function oroborosFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers || {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (!headers.has('X-Requested-With')) headers.set('X-Requested-With', 'Oroboros');
  return artificeFetch(path, {
    ...init,
    headers,
    apiBaseUrl: oroborosBrand.apiBaseUrl,
  });
}

/** Drop snapshotted access JWTs so a 401 retry picks up the token from the refresh that just ran. */
function withoutSnapshottedAccessHeaders(init?: RequestInit): RequestInit | undefined {
  if (!init?.headers) return init;
  const headers = new Headers(init.headers);
  headers.delete('Authorization');
  headers.delete('X-Artifice-Access');
  headers.delete('X-Jester-Access');
  return { ...init, headers };
}

export type OroborosJsonResult<T = any> = {
  ok: boolean;
  status: number;
  data: T;
  /** Set on network failure or empty/non-JSON body — not a parsed server field. */
  error?: string;
  retryAfterSec?: number;
};

/** Auto-retry 429s that the server says will clear in this window (Agent prefs bursts often return 1s). */
const SHORT_429_RETRY_MS = 2000;

function readRetryAfterHeader(res: Response): string | null {
  try {
    return typeof res.headers?.get === 'function' ? res.headers.get('Retry-After') : null;
  } catch {
    return null;
  }
}

export function parseRetryAfterSec(data: unknown, retryAfterHeader?: string | null): number | null {
  const body = data && typeof data === 'object' ? (data as { retryAfterSec?: unknown }).retryAfterSec : undefined;
  const fromBody = Number(body);
  if (Number.isFinite(fromBody) && fromBody >= 0) return fromBody;
  if (retryAfterHeader != null && String(retryAfterHeader).trim()) {
    const fromHeader = Number(retryAfterHeader);
    if (Number.isFinite(fromHeader) && fromHeader >= 0) return fromHeader;
  }
  return null;
}

function failClosedBody<T>(status: number, error: string): OroborosJsonResult<T> {
  return {
    ok: false,
    status,
    data: { success: false, error } as T,
    error,
  };
}

async function oroborosJsonOnce<T>(
  path: string,
  init?: RequestInit,
): Promise<OroborosJsonResult<T>> {
  const res = await oroborosFetch(path, init);
  let text = '';
  try {
    text = await res.text();
  } catch {
    return failClosedBody<T>(res.status, 'Empty or non-JSON response');
  }
  if (!String(text).trim()) {
    return failClosedBody<T>(res.status, 'Empty or non-JSON response');
  }
  try {
    const data = JSON.parse(text) as T;
    const retryAfterSec = parseRetryAfterSec(data, readRetryAfterHeader(res)) ?? undefined;
    return { ok: res.ok, status: res.status, data, retryAfterSec };
  } catch {
    return failClosedBody<T>(res.status, 'Empty or non-JSON response');
  }
}

function isSafeIdempotentMethod(init?: RequestInit): boolean {
  const method = String(init?.method || 'GET').toUpperCase();
  return method === 'GET' || method === 'HEAD';
}

function isTransientGatewayStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

export async function oroborosJson<T = any>(
  path: string,
  init?: RequestInit,
): Promise<OroborosJsonResult<T>> {
  try {
    let first = await oroborosJsonOnce<T>(path, init);
    if (isTransientGatewayStatus(first.status) && isSafeIdempotentMethod(init)) {
      await new Promise((resolve) => setTimeout(resolve, 350));
      first = await oroborosJsonOnce<T>(path, init);
    }
    const retryAfterSec = first.retryAfterSec ?? parseRetryAfterSec(first.data);
    if (first.status === 429 && retryAfterSec != null && retryAfterSec * 1000 <= SHORT_429_RETRY_MS) {
      await new Promise((resolve) => setTimeout(resolve, Math.max(50, retryAfterSec * 1000 + 50)));
      first = await oroborosJsonOnce<T>(path, init);
    }
    if (first.status !== 401) return first;
    if (path.includes('/api/terminal/session/refresh') || path.includes('/api/terminal/session/logout')) {
      return first;
    }
    try {
      const { isTerminalSessionRefreshBlocked } = await import('../../utils/terminalSessionRefresh');
      if (isTerminalSessionRefreshBlocked()) return first;
    } catch {
      /* continue to refresh */
    }
    try {
      const { refreshOroborosSession } = await import('./session');
      const refreshed = await refreshOroborosSession();
      if (!refreshed.success) return first;
    } catch {
      return first;
    }
    return oroborosJsonOnce<T>(path, withoutSnapshottedAccessHeaders(init));
  } catch (err) {
    const message =
      err instanceof Error && String(err.message || '').trim()
        ? `Network error: ${err.message}`
        : 'Network error';
    return failClosedBody<T>(0, message);
  }
}
